import { useState, useEffect, useCallback } from 'react';
import { useGraphQL } from '../../../../apollo/useGraphQL';
import {
    GET_BRANCH_COMPANIES, GET_EQUIPMENT_DATA,
    GET_SENSOR_DATA,
    SAVE_SENSOR_DEVICE_SETTINGS
} from '../graphql/queries';
import Message from "../../../../utils/message/Message";

/**
 * KPI 설정 관리 hook
 * 
 * @returns {Object} KPI 설정 관련 상태 및 함수들
 */
export const useSensorSetting = () => {
    const [branchList, setBranchList] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedBranch, setSelectedBranch] = useState(null); // 선택된 지부
    const [equipmentList, setEquipmentList] = useState([]);

    const [sensorDeviceList, setSensorDeviceList] = useState([]);
    const { executeQuery, executeMutation } = useGraphQL();

    const fetchSensor = async (branchId) => {
        try {
            //센서정보 데이터 가져오기
            const sensorData = await executeQuery(GET_SENSOR_DATA,{filter: branchId});
            // 데이터 구조 가공
            const sensors = sensorData.data.getSensorData.map(sensor =>({
                id : sensor.seq,
                site : sensor.site,
                compCd: sensor.compCd,
                companyName: sensor.companyName,
                deviceId: sensor.deviceId,
                deviceName: sensor.deviceName,
                attachedEquipmentId: sensor.attachedEquipmentId,
                equipmentName: sensor.equipmentName,
                createUser: sensor.createUser,
                createDate: sensor.createDate,
                updateUser: sensor.updateUser,
                updateDate: sensor.updateDate,

            }));
            setSensorDeviceList(sensors);
        }catch (error) {
            console.error('Error fetching data:', error);
            // 다른 에러만 메시지 표시
            if (error.message) {
                Message.showError('데이터 초기화 중 오류가 발생했습니다.');
            }
        }
    }

    const fetchEquipments = async (branchId) => {
        try {
            //설비정보 데이터 가져오기
            const equipData = await executeQuery(GET_EQUIPMENT_DATA,{site: branchId});
            // 데이터 구조 가공
            const equips = equipData.data.getEquipmentData.map(equip =>({
                id: equip.equipmentId,
                equipmentName: equip.equipmentName
            }));
            setEquipmentList(equips);

        }catch (error) {
            console.error('Error fetching data:', error);
            // 다른 에러만 메시지 표시
            if (error.message) {
                Message.showError('데이터 초기화 중 오류가 발생했습니다.');
            }
        }
    }

    const fetchData = async () => {
        setIsLoading(true);
        try {
            // 지점 및 회사 정보 조회
            const branchResult = await executeQuery(GET_BRANCH_COMPANIES);

            // 권한 에러로 인해 데이터가 null인 경우 처리
            if (branchResult === null) {
                setIsLoading(false);
                return;
            }

            if (branchResult?.data) {
                // 지부 및 회사 데이터 구조 가공
                const branches = branchResult.data.getBranchCompanies.map(branch => ({
                    id: branch.id,
                    name: branch.name,
                }));

                setBranchList(branches);

            } else {
                throw new Error('데이터가 올바르게 조회되지 않았습니다.');
            }
        } catch (error) {
            console.error('Error fetching data:', error);

            // 다른 에러만 메시지 표시
            if (error.message) {
                Message.showError('데이터 초기화 중 오류가 발생했습니다.');
            }
        } finally {
            setIsLoading(false);
        }
    };


    // 데이터 초기화
    useEffect(() => {
        fetchData();
    }, []);

    /**
     * 지부 선택 처리
     * @param {string} branchId - 선택된 지부 ID
     */
    const handleBranchChange = useCallback(async (branchId) => {
        setSelectedBranch(branchId);
        fetchSensor(branchId);
        fetchEquipments(branchId);

    }, []);

    /**
     * 설비정보 수정 처리
     * @param {SensorDeviceRequest} req - 선택된 설비 정보
     */
    const handleSensorDeviceSave = useCallback(async (SensorDeviceRequest, branchId) => {

        const result = await executeMutation(SAVE_SENSOR_DEVICE_SETTINGS,{req: SensorDeviceRequest});

        if (result?.data?.saveSensorDeviceSettings?.success) {
            Message.showSuccess('정보가 수정되었습니다.');

            // 저장 성공 시 초기 데이터 업데이트
            fetchSensor(branchId);
        } else {
            throw new Error(result?.data?.saveKpiSettings?.message || '저장 중 오류가 발생했습니다.');
        }
    }, []);

    return {
        branchList,
        selectedBranch,
        handleBranchChange,
        isLoading,

        //설비정보 관련
        sensorDeviceList,
        equipmentList,
        handleSensorDeviceSave
    };
}; 